import type { SessionStatus } from "../domain/session.ts";
import type {
  NativeConnectionState,
  NativeProcessState,
} from "./domain.ts";

/** The input/control state owned by the terminal authority. */
export type NativeControlState =
  | "none"
  | "acquiring"
  | "owned"
  | "held_by_other"
  | "expired"
  | "rejected";

export interface NativeTerminalLifecycleTarget {
  sessionId: string;
  processAttemptId: string | null;
  generation: number;
}

export interface NativeTerminalLifecycleState {
  target: NativeTerminalLifecycleTarget;
  process: NativeProcessState;
  connection: NativeConnectionState;
  control: NativeControlState;
  /** At least one authoritative VT snapshot has been painted. */
  screenReady: boolean;
  /** A previous generation is still painted while the new one attaches. */
  staleScreen: boolean;
  recoverableError: string | null;
}

export type NativeTerminalLifecycleEvent =
  | {
      type: "projection";
      sessionId: string;
      status: SessionStatus;
      processAttemptId?: string | null;
      generation: number;
    }
  | { type: "start_requested"; generation?: number }
  | { type: "stop_requested" }
  | { type: "restart_requested"; generation?: number }
  | { type: "connection"; state: NativeConnectionState; generation?: number }
  | { type: "control"; state: NativeControlState; generation?: number }
  | { type: "snapshot_ready"; generation?: number }
  | { type: "recoverable_error"; message: string; generation?: number }
  | { type: "clear_error" };

export type NativeTerminalIntent =
  | "none"
  | "start_and_connect"
  | "connect"
  | "reconnect";

/**
 * Convert a Session projection to the process state used by every terminal
 * client. `waiting` is a running process; it must never be used as a socket
 * or lease state.
 */
export function nativeProcessStateFromStatus(status: SessionStatus): NativeProcessState {
  switch (status) {
    case "starting": return "starting";
    case "running":
    case "waiting": return "running";
    case "stopping": return "stopping";
    case "stopped":
    case "archived": return "stopped";
    case "exited": return "exited";
    case "error": return "failed";
  }
}

function targetFor(event: NativeTerminalLifecycleEvent, state: NativeTerminalLifecycleState): boolean {
  if (event.type === "projection") return event.sessionId === state.target.sessionId;
  if (event.type === "start_requested" || event.type === "restart_requested") {
    return true;
  }
  return !("generation" in event)
    || event.generation === undefined
    || event.generation === state.target.generation;
}

/**
 * One deterministic reducer for lifecycle facts. UI events are intents; only
 * projections and relay events can move a process/connection to a settled
 * state. Older generations are ignored rather than clearing the current VT.
 */
export function reduceNativeTerminalLifecycle(
  state: NativeTerminalLifecycleState,
  event: NativeTerminalLifecycleEvent,
): NativeTerminalLifecycleState {
  if (!targetFor(event, state)) return state;
  switch (event.type) {
    case "projection": {
      if (event.generation < state.target.generation) return state;
      const nextProcess = nativeProcessStateFromStatus(event.status);
      const targetChanged = event.generation !== state.target.generation
        || (event.processAttemptId ?? null) !== state.target.processAttemptId;
      const settled = nextProcess === "running";
      return {
        ...state,
        target: {
          sessionId: state.target.sessionId,
          processAttemptId: event.processAttemptId ?? null,
          generation: event.generation,
        },
        process: nextProcess,
        connection: settled
          ? (targetChanged ? "reconnecting" : state.connection)
          : nextProcess === "starting" ? "connecting" : "disconnected",
        control: targetChanged ? "none" : state.control,
        screenReady: targetChanged ? false : state.screenReady,
        staleScreen: targetChanged && settled ? true : state.staleScreen,
        recoverableError: nextProcess === "failed" || nextProcess === "exited"
          ? state.recoverableError
          : null,
      };
    }
    case "start_requested":
      return {
        ...state,
        process: "starting",
        connection: "connecting",
        control: "none",
        staleScreen: state.process === "running" || state.staleScreen,
        screenReady: state.process === "running" ? state.screenReady : false,
        recoverableError: null,
        target: { ...state.target, generation: event.generation ?? state.target.generation },
      };
    case "stop_requested":
      return {
        ...state,
        process: "stopping",
        connection: "disconnected",
        control: "none",
        recoverableError: null,
      };
    case "restart_requested":
      return {
        ...state,
        process: "starting",
        connection: "connecting",
        control: "none",
        staleScreen: true,
        screenReady: false,
        recoverableError: null,
        target: { ...state.target, generation: event.generation ?? state.target.generation + 1 },
      };
    case "connection":
      return { ...state, connection: event.state, recoverableError: event.state === "connected" ? null : state.recoverableError };
    case "control":
      return { ...state, control: event.state };
    case "snapshot_ready":
      return { ...state, screenReady: true, staleScreen: false };
    case "recoverable_error":
      return {
        ...state,
        connection: state.connection === "connected" ? "reconnecting" : state.connection,
        recoverableError: event.message,
      };
    case "clear_error":
      return { ...state, recoverableError: null };
  }
}

export function nativeTerminalLifecycleFromSession(input: {
  id: string;
  status: SessionStatus;
  processAttemptId?: string | null;
  generation: number;
  connection?: NativeConnectionState;
  control?: NativeControlState;
}): NativeTerminalLifecycleState {
  return {
    target: {
      sessionId: input.id,
      processAttemptId: input.processAttemptId ?? null,
      generation: input.generation,
    },
    process: nativeProcessStateFromStatus(input.status),
    connection: input.connection ?? "disconnected",
    control: input.control ?? "none",
    staleScreen: false,
    screenReady: false,
    recoverableError: null,
  };
}

/** Determine the single lifecycle button/intent shown for a Session. */
export function nativeTerminalIntent(input: {
  status: SessionStatus;
  connection: NativeConnectionState;
  requested?: boolean;
  retry?: boolean;
}): NativeTerminalIntent {
  if (["stopped", "exited", "error", "archived"].includes(input.status)) return "start_and_connect";
  if (input.retry) return "reconnect";
  if (["running", "waiting", "starting"].includes(input.status)) {
    if (input.connection === "connection_failed" || input.connection === "disconnected") return "connect";
    if (input.connection === "reconnecting") return "reconnect";
    return "none";
  }
  return input.requested ? "reconnect" : "none";
}
